<template>
  <div class="mt-4 mb-4">
    <!-- Title and scroll buttons container -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <!-- Title -->
      <h2 class="mb-0 popular-recipes-title">Популярные рецепты</h2>

      <!-- Scroll buttons -->
      <div
        v-if="recipes.length > 0 && (canScrollLeft || canScrollRight)"
        class="scroll-buttons-container d-flex"
      >
        <button
          v-show="canScrollLeft"
          @click="scrollContent('left')"
          class="btn btn-sm btn-outline-secondary scroll-button me-2"
          title="Прокрутить влево"
        >
          <i class="bi bi-chevron-left"></i>
        </button>
        <button
          v-show="canScrollRight"
          @click="scrollContent('right')"
          class="btn btn-sm btn-outline-secondary scroll-button"
          title="Прокрутить вправо"
        >
          <i class="bi bi-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- Loading indicator -->
    <div v-if="isLoading" class="text-center">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Загрузка популярных рецептов...</span>
      </div>
    </div>

    <!-- Error message -->
    <div v-else-if="error" class="alert alert-warning" role="alert">
      {{ error }}
    </div>

    <!-- Recipe cards -->
    <div
      v-else-if="recipes.length > 0"
      class="recipes-scroll-list"
      ref="scrollList"
    >
      <RecipeCardPopular
        v-for="recipe in recipes"
        :key="recipe.id"
        :recipe="recipe"
        @show-full-recipe="handleShowFullRecipe"
      />
    </div>
    <div v-else class="text-muted">Популярных рецептов пока нет.</div>
  </div>
</template>

<script>
import RecipeCardPopular from "./RecipeCardPopular.vue";

const CMD_RECIPE_GET_POPULAR = "recipe.getPopular";
const POPULAR_RECIPES_LIMIT = 30;

export default {
  name: "PopularRecipes",
  components: {
    RecipeCardPopular,
  },
  data() {
    return {
      recipes: [],
      isLoading: false,
      error: null,
      canScrollLeft: false,
      canScrollRight: false,
      scrollAmount: 202, // Approx width of one card (187px) + margin (15px)
    };
  },
  async mounted() {
    await this.fetchPopularRecipes();
  },
  methods: {
    async fetchPopularRecipes() {
      // Reset scroll button states before fetching new recipes
      // as the scrollability might change.
      this.canScrollLeft = false;
      this.canScrollRight = false;
      // Detach scroll listener if it exists from a previous population
      const oldScrollListEl = this.$refs.scrollList;
      if (oldScrollListEl) {
        oldScrollListEl.removeEventListener(
          "scroll",
          this.updateScrollButtonsState
        );
      }
      this.isLoading = true;
      this.error = null;
      try {
        const command = `${CMD_RECIPE_GET_POPULAR}/${POPULAR_RECIPES_LIMIT}`;
        const rawData = await this.sendCmd(command, null);
        const data = JSON.parse(rawData);

        if (Array.isArray(data)) {
          this.recipes = data;
        } else {
          console.warn("PopularRecipes: Expected an array, got:", data);
          this.recipes = [];
          // Optionally set an error message if the data format is unexpected
          // this.error = "Не удалось загрузить популярные рецепты: неверный формат данных.";
        }
      } catch (err) {
        console.error("PopularRecipes: Error fetching popular recipes:", err);
        this.error = "Не удалось загрузить популярные рецепты.";
        this.recipes = [];
      } finally {
        this.isLoading = false;
        // Use nextTick to ensure $refs.scrollList is available and populated
        // before attaching listener and checking scroll state.
        this.$nextTick(() => {
          const scrollListEl = this.$refs.scrollList;
          if (scrollListEl && this.recipes.length > 0) {
            scrollListEl.addEventListener(
              "scroll",
              this.updateScrollButtonsState,
              { passive: true }
            );
            this.updateScrollButtonsState(); // Initial check
          }
        });
      }
    },
    scrollContent(direction) {
      const listElement = this.$refs.scrollList;
      if (listElement) {
        const amount =
          direction === "left" ? -this.scrollAmount : this.scrollAmount;
        listElement.scrollBy({ left: amount, behavior: "smooth" });
        // The scroll event will trigger updateScrollButtonsState after scroll
      }
    },
    updateScrollButtonsState() {
      const el = this.$refs.scrollList;
      if (el) {
        const tolerance = 1; // To handle floating point inaccuracies
        this.canScrollLeft = el.scrollLeft > tolerance;
        this.canScrollRight =
          el.scrollLeft < el.scrollWidth - el.clientWidth - tolerance;
      } else {
        this.canScrollLeft = false;
        this.canScrollRight = false;
      }
    },
    handleShowFullRecipe(recipe) {
      this.$emit("show-full-recipe", recipe);
    },
  },
};
</script>

<style scoped>
.popular-recipes-title {
  /* Ensure title doesn't push buttons too far on small screens if it wraps */
  min-width: 0;
}
.scroll-buttons-container {
  /* Prevent buttons from shrinking if title is long */
  flex-shrink: 0;
}
.recipes-scroll-list {
  display: flex;
  overflow-x: auto;
  padding-bottom: 10px; /* For scrollbar visibility if needed */
  gap: 0.98em;
}

/* --- Styles for Scrollbar Visibility (Test) --- */
/* WebKit browsers (Chrome, Safari, Edge) */
.recipes-scroll-list::-webkit-scrollbar {
  display: block; /* Make scrollbar visible */
  height: 8px;
}
.recipes-scroll-list::-webkit-scrollbar-track {
  /* Use a subtle background color, similar to component backgrounds */
  background-color: var(--bs-body-bg, #fff); /* Default to body background */
}
.recipes-scroll-list::-webkit-scrollbar-thumb {
  /* Use a color that contrasts with the track, like text or border color */
  background-color: var(
    --bs-secondary-bg,
    #e9ecef
  ); /* A slightly darker background, or a subtle color */
  border-radius: 4px; /* Optional: rounded corners for the thumb */
}
.recipes-scroll-list::-webkit-scrollbar-thumb:hover {
  background-color: var(
    --bs-primary-bg-subtle,
    #cfe2ff
  ); /* A subtle primary color for hover */
}
/* Firefox */
.recipes-scroll-list {
  -ms-overflow-style: none; /* For Internet Explorer and Edge */
  scrollbar-width: thin; /* Make scrollbar visible and thin for Firefox */
  /* scrollbar-color: thumbColor trackColor; uses Bootstrap variables */
  scrollbar-color: var(--bs-secondary-bg, #e9ecef) var(--bs-body-bg, #fff);
}
.scroll-button i {
  font-size: 1.2rem;
}
</style>
